import React, {ReactElement} from "react";

import MarkdownText, {
  MARKDOWN_TYPES,
} from "../markdown/markdown-text/MarkdownText";
import Card from "../cards/card/Card";
import ModuleBody from "../layouts/module-body/ModuleBody";
import Video, {VideoType} from "../videos/video/Video";
import WidgetBreadcrumbs from "../widgets/widget-breadcrumbs/WidgetBreadcrumbs";
import WidgetFastFacts from "../widgets/widget-fast-facts/WidgetFastFacts";
import WidgetForm from "../widgets/widget-form/WidgetForm";
import WidgetGetStarted from "../widgets/widget-get-started/WidgetGetStarted";
import WidgetMarkdown from "../widgets/widget-markdown/WidgetMarkdown";
import WidgetShare from "../widgets/widget-share/WidgetShare";
import HeaderInfo from "../header-info/HeaderInfo";
import Image, {ImageObject} from "../image/Image";
import Quote from "../cards/quote/Quote";
import Callout from "../callout/Callout";

import {
  css,
  generateKeyId,
  getArticleBackBtnLabel,
  getContentRootPathBasedOnArticleType,
  visibleForLocale,
} from "/src/utils/utils";
import translate from "/src/utils/translate";
import {formattedPublishDate} from "/src/utils/blog";
import {LOCALE} from "/src/config";
import {SimpleFormProps} from "/src/types";
import CTAButton, {CTA} from "../buttons/cta-button/CTAButton";
import {NO_CATEGORY_TYPES} from "/src/constants/common";
import CardLayout from "../layouts/card-layout/CardLayout";

import * as styles from "./flexible-base-content-model.module.scss";

export type FlexibleBaseContentModelContentModule =
  | Queries.FlexibleBaseContentModelFragmentFragment["contentModules"]
  | NonNullable<
      Queries.BlogPostTemplateQueryQuery["contentfulBlogPost"]
    >["contentModules"];

export interface FlexibleBaseContentModelProps
  extends Omit<
    Queries.FlexibleBaseContentModelFragmentFragment,
    "__typename" | "contentModules"
  > {
  slug: string;
  contentModules: FlexibleBaseContentModelContentModule;
  skipHeaderTags?: boolean;
}

interface FlexibleBaseContentModelAuthorProfile {
  name?: string;
  description?: string;
  photo?: ImageObject;
  slug: string;
  title?: string;
  showHeader?: boolean;
  position?: "top" | "bottom";
}

const AuthorProfile = (
  props: FlexibleBaseContentModelAuthorProfile
): ReactElement => {
  const {name, description, photo, slug, title, showHeader, position} = props;

  return (
    <div
      className={css(
        position === "bottom"
          ? styles.flexibleBaseContentModelAuthorBottom
          : styles.flexibleBaseContentModelAuthorTop
      )}
    >
      {showHeader && (
        <h4
          className={css("h4", styles.flexibleBaseContentModelAuthor__header)}
        >
          {translate("About the Author")}
        </h4>
      )}

      <div className={styles.flexibleBaseContentModelAuthor__body}>
        {photo && (
          <div className={styles.flexibleBaseContentModelAuthor__imgWrapper}>
            <Image
              objectFit="cover"
              image={photo}
              classes={styles.flexibleBaseContentModelAuthor__img}
            />
          </div>
        )}

        <div className={styles.flexibleBaseContentModelAuthor__info}>
          {name && slug ? (
            <CTAButton
              kind="tertiary"
              classes={css(
                styles.flexibleBaseContentModelAuthor__name,
                styles.flexibleBaseContentModelAuthor__link
              )}
              cta={{
                label: name,
                url: `/blog/author/${slug}`,
              }}
            />
          ) : (
            <p
              className={css("p", styles.flexibleBaseContentModelAuthor__name)}
            >
              {name}
            </p>
          )}
          {title && (
            <p
              className={css("p", styles.flexibleBaseContentModelAuthor__title)}
            >
              {title}
            </p>
          )}
          {description && (
            <p
              className={css("p", styles.flexibleBaseContentModelAuthor__desc)}
            >
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const FlexibleBaseContentModel = (
  props: FlexibleBaseContentModelProps
): ReactElement => {
  const {
    articleType,
    author,
    showSocialMediaIcons,
    categoryTag,
    contentModules,
    slug,
    featuredContent,
    publishDate,
    rightSectionWidgets,
    shareLink,
    title,
    metaDescription,
    socialMediaImage,
    skipHeaderTags,
  } = props;

  const breadcrumbsRootLink = getContentRootPathBasedOnArticleType(
    articleType! || "blog"
  );

  const breadcrumbsLinks = [
    // add root crumbs based on article type
    {
      label: getArticleBackBtnLabel(articleType || "home"),
      href: breadcrumbsRootLink,
    },
  ];

  if (
    !NO_CATEGORY_TYPES.includes(articleType!) &&
    categoryTag &&
    categoryTag.title &&
    categoryTag.slug
  ) {
    // add category crumbs if any
    breadcrumbsLinks.push({
      label: categoryTag.title,
      href: `${breadcrumbsRootLink}/category/${categoryTag.slug}`,
    });
  }

  if (title && slug) {
    // add current page title to crumbs
    breadcrumbsLinks.push({
      label: title,
      href: `${breadcrumbsRootLink}/${slug}`,
    });
  }

  return (
    <ModuleBody classes={styles.flexibleBaseContentModel}>
      {!skipHeaderTags && (
        <HeaderInfo
          title={title!}
          description={metaDescription!}
          image={socialMediaImage as ImageObject}
        />
      )}

      <section className={styles.flexibleBaseContentModel__body}>
        <header className={styles.flexibleBaseContentModel__header}>
          <WidgetBreadcrumbs links={breadcrumbsLinks} />

          <h1
            className={css(
              "h1",
              styles.flexibleBaseContentModel__header__title
            )}
          >
            {title}
          </h1>

          {author && (
            <AuthorProfile
              name={author.name!}
              photo={author.headshot as ImageObject}
              slug={author.slug!}
              position="top"
              title={author.title!}
              description={
                publishDate ? formattedPublishDate(publishDate)! : ""
              }
            />
          )}

          {showSocialMediaIcons && (
            <WidgetShare
              link={shareLink!}
              classes={styles.flexibleBaseContentModel__header__widget}
            />
          )}
        </header>

        <section className={css(styles.flexibleBaseContentModel__content)}>
          {(contentModules || []).map((module, i) => {
            if (!module) {
              return null;
            }

            const typeName = module?.__typename!;
            const key = generateKeyId(
              `flexible-base-content-model-${typeName || "module"}-${i}`
            );

            switch (typeName) {
              case "ContentfulContentPageBodyCopy": {
                const temp =
                  module as unknown as Queries.ContentfulContentPageBodyCopy;
                return (
                  <MarkdownText
                    type={MARKDOWN_TYPES.ARTICLE}
                    text={temp.body?.body!}
                    key={key}
                  />
                );
              }

              case "ContentfulContentPageVideo": {
                const temp =
                  module as unknown as Queries.ContentfulContentPageVideo;

                let type = "inline";
                if (temp.popup) {
                  type = "popup";
                } else if (temp.autoplay) {
                  type = "autoplay";
                }

                return (
                  <div
                    className={styles.flexibleBaseContentModel__videoWrapper}
                  >
                    <Video
                      videoTitle={title!}
                      key={key}
                      showControl={temp.videoControls === true}
                      type={type as VideoType}
                      videoSource={temp.videoSource!}
                      caption={temp.caption?.caption!}
                    />
                  </div>
                );
              }

              case "ContentfulContentPagePullQuote": {
                const temp =
                  module as unknown as Queries.ContentfulContentPagePullQuote;
                return (
                  <Quote
                    key={key}
                    name={temp.authorName!}
                    role={temp.authorTitle!}
                    quote={temp.quoteText?.quoteText!}
                  />
                );
              }

              case "ContentfulCallout": {
                const temp = module as unknown as Queries.ContentfulCallout;
                return (
                  <Callout
                    headline={temp.copy?.copy!}
                    cta={temp.ctaLink as CTA}
                    image={temp.thumbnail as unknown as ImageObject}
                    key={key}
                  />
                );
              }

              default:
                return null;
            }
          })}

          {author && (
            <AuthorProfile
              name={author.name!}
              photo={author.headshot as ImageObject}
              slug={author.slug!}
              position="bottom"
              showHeader
              title={author.title!}
              description={
                publishDate ? formattedPublishDate(publishDate)! : ""
              }
            />
          )}
        </section>
        <section className={css(styles.flexibleBaseContentModel__widgets)}>
          {(rightSectionWidgets || []).map((module, i) => {
            const key = generateKeyId(
              `flexible-base-content-model-right-sections-${i}`
            );
            switch (module?.__typename) {
              case "ContentfulFastFacts":
                return (
                  <WidgetFastFacts
                    key={key}
                    eyebrow={module.mainEyebrow!}
                    stats={[
                      {
                        headline: module.leftHeadline!,
                        copy: module.leftBodyCopy?.leftBodyCopy!,
                      },
                      {
                        headline: module.middleHeadline!,
                        copy: module.middleBodyCopy?.middleBodyCopy!,
                      },
                      {
                        headline: module.rightHeadline!,
                        copy: module.rightBodyCopy?.rightBodyCopy!,
                      },
                    ]}
                    logo={module.mainLogo as ImageObject}
                  />
                );

              case "ContentfulContentPageBodyCopy":
                return <WidgetMarkdown key={key} text={module?.body?.body!} />;

              case "ContentfulGetStartedModule": {
                // add check for visible in locale

                if (
                  !visibleForLocale(module?.visibleIn as Array<string>, LOCALE)
                ) {
                  return null;
                }

                return module.form != null ? (
                  <WidgetForm
                    key={key}
                    body={module.body?.body!}
                    form={module?.form as SimpleFormProps}
                    headline={module?.title!}
                    image={module?.image as ImageObject}
                  />
                ) : (
                  <WidgetGetStarted
                    key={key}
                    copy={module.body?.body!}
                    headline={module.title!}
                    ctas={module?.ctaButtons as unknown as Array<CTA>}
                  />
                );
              }

              default:
                return null;
            }
          })}
        </section>
      </section>

      {featuredContent != null && featuredContent.cards != null && (
        <section className={css(styles.flexibleBaseContentModel__featured)}>
          <h2 className={css("h2", styles.flexibleBaseContentModel__featured)}>
            {translate("Related content")}
          </h2>

          <CardLayout
            items={(featuredContent.cards || []).map((card, i) => {
              // GROW-3148
              // check if ctaLink exists, use this as fallback
              let firstCta = card?.ctaLink;

              // if ctaButtons exists, then we will use
              // the first ctaButtons instead
              if (card?.ctaButtons) {
                firstCta =
                  card != null && card.ctaButtons != null
                    ? card.ctaButtons[0]
                    : null;
              }
              const cta =
                firstCta != null
                  ? {
                      label: firstCta?.label,
                      url: firstCta.url,
                      amplitudeCtaProps: {
                        name:
                          firstCta.amplitudeCtaName ||
                          "blog_post_related_content",
                        type: firstCta.amplitudeCtaType || "link",
                        contentType: "blogPost",
                        linkUrl: firstCta.url || "",
                      },
                    }
                  : null;

              const key = generateKeyId(
                `flexible-content-model-featured-${card?.title!}-${i}`
              );

              return (
                <Card
                  key={key}
                  copy={card?.copy?.copy!}
                  ctas={[cta as CTA]}
                  eyebrow={card?.eyebrow!}
                  headline={card?.title!}
                  classes={styles.flexibleBaseContentModel__featured__card}
                  gatsbyImageWrapperClasses={
                    styles.flexibleBaseContentModel__featured__cardImg
                  }
                  image={card?.image as ImageObject}
                />
              );
            })}
            column="3"
          />
        </section>
      )}
    </ModuleBody>
  );
};

export default FlexibleBaseContentModel;
